import React, { useRef, useState } from 'react';
import { COLUMN_COLORS, COLUMN_PICKER_COLORS } from '../../../constants/colorConstants';
import { TaskColumn } from '../../../types/topic';
import { ArrowLeft, ArrowRight, Circle, MoreHorizontal, Plus, Trash } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Dropdown, DropdownContent, DropdownDivider, DropdownItem } from '../../../components/ui';
import { useViewport } from '../../../contexts/ViewportContext';
import {
  MobileFullScreenModal,
  ModalDoneFooter,
} from '../../../components/ui/MobileFullScreenModal';

interface ColumnMenuProps {
  column: TaskColumn;
  isFirst: boolean;
  isLast: boolean;
  onDeleteClick: (columnId: string) => void;
  onEditNameClick?: (columnId: string) => void;
  onColorSelect: (columnId: string, color: string) => void;
  onAddColumn: (afterColumnId: string) => void;
  onMoveLeft: (columnId: string) => void;
  onMoveRight: (columnId: string) => void;
}

export const ColumnMenu: React.FC<ColumnMenuProps> = ({
  column,
  isFirst,
  isLast,
  onDeleteClick,
  onColorSelect,
  onAddColumn,
  onMoveLeft,
  onMoveRight,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useViewport();

  // Close menu handler
  const handleClose = () => {
    setIsOpen(false);
  };

  // Column color selection handler
  const handleColorSelect = (color: string) => {
    onColorSelect(column.id, color);
    setIsOpen(false);
  };

  // Add column handler
  const handleAddColumn = () => {
    onAddColumn(column.id);
    setIsOpen(false);
  };

  // Delete column handler
  const handleDeleteColumn = () => {
    onDeleteClick(column.id);
    setIsOpen(false);
  };

  // Move column left handler
  const handleMoveLeft = () => {
    if (!isFirst) {
      onMoveLeft(column.id);
      setIsOpen(false);
    }
  };

  // Move column right handler
  const handleMoveRight = () => {
    if (!isLast) {
      onMoveRight(column.id);
      setIsOpen(false);
    }
  };

  // Render the mobile full-screen modal version
  if (isMobile && isOpen) {
    return (
      <>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-500"
          aria-label="Column options"
        >
          <MoreHorizontal size={16} />
        </button>

        <MobileFullScreenModal
          isOpen={isOpen}
          onClose={handleClose}
          title="Column Options"
          footerContent={<ModalDoneFooter onClose={handleClose} />}
        >
          {/* Color selection section */}
          <div>
            <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
              <h3 className="font-medium text-gray-700">Color</h3>
            </div>

            <div className="p-4 flex flex-wrap gap-3 justify-center">
              {COLUMN_COLORS.map((color, index) => (
                <button
                  key={index}
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    isMobile && 'w-8 h-8',
                    COLUMN_PICKER_COLORS[index],
                    column.color === color && 'ring-3 ring-blue-500'
                  )}
                  title={`Color ${index + 1}`}
                >
                  {column.color === color && (
                    <Circle size={8} className="text-blue-500 fill-current" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Actions section */}
          <div className="mt-2">
            <div className="px-4 py-3 bg-gray-50 flex justify-between items-center">
              <h3 className="font-medium text-gray-700">Actions</h3>
            </div>

            {/* Move column left */}
            <button
              onClick={handleMoveLeft}
              disabled={isFirst}
              className={cn(
                'flex items-center w-full px-4 py-3 text-left',
                isFirst ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
              )}
            >
              <div className="flex-1 flex items-center gap-2">
                <ArrowLeft size={16} className="text-gray-600" />
                <span>Move left</span>
              </div>
            </button>

            {/* Move column right */}
            <button
              onClick={handleMoveRight}
              disabled={isLast}
              className={cn(
                'flex items-center w-full px-4 py-3 text-left',
                isLast ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
              )}
            >
              <div className="flex-1 flex items-center gap-2">
                <ArrowRight size={16} className="text-gray-600" />
                <span>Move right</span>
              </div>
            </button>

            {/* Add column */}
            <button
              onClick={handleAddColumn}
              className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex-1 flex items-center gap-2">
                <Plus size={16} className="text-blue-500" />
                <span>Add column after this one</span>
              </div>
            </button>

            {/* Delete column */}
            <button
              onClick={handleDeleteColumn}
              className="flex items-center w-full px-4 py-3 text-left hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex-1 flex items-center gap-2">
                <Trash size={16} className="text-red-600" />
                <span className="text-red-600">Delete column</span>
              </div>
            </button>
          </div>
        </MobileFullScreenModal>
      </>
    );
  }

  // Render desktop dropdown version
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-500"
        aria-label="Column options"
      >
        <MoreHorizontal size={16} />
      </button>

      <Dropdown
        trigger={<span style={{ display: 'none' }} />}
        open={isOpen}
        onOpenChange={setIsOpen}
        width={276}
        position="bottom-left"
        anchorElement={buttonRef.current}
        contentClassName="shadow-lg rounded-md py-1 overflow-hidden border-0"
      >
        <DropdownContent>
          <div className="px-3 py-2">
            <div className="flex justify-center gap-1.5">
              {COLUMN_COLORS.map((color, index) => (
                <button
                  key={index}
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    'w-6 h-6 rounded-full flex items-center justify-center',
                    // Use the darker picker colors for better visibility
                    COLUMN_PICKER_COLORS[index],
                    column.color === color && 'ring-2 ring-blue-500'
                  )}
                  title={`Color ${index + 1}`}
                >
                  {column.color === color && (
                    <Circle size={6} className="text-blue-500 fill-current" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <DropdownDivider />

          <DropdownItem
            leftIcon={<ArrowLeft size={14} className={isFirst ? 'text-gray-300' : ''} />}
            onClick={handleMoveLeft}
            disabled={isFirst}
            className={isFirst ? 'opacity-40 cursor-not-allowed' : ''}
          >
            Move left
          </DropdownItem>

          <DropdownItem
            leftIcon={<ArrowRight size={14} className={isLast ? 'text-gray-300' : ''} />}
            onClick={handleMoveRight}
            disabled={isLast}
            className={isLast ? 'opacity-40 cursor-not-allowed' : ''}
          >
            Move right
          </DropdownItem>

          <DropdownDivider />

          <DropdownItem leftIcon={<Plus size={14} />} onClick={handleAddColumn}>
            Add column after this one
          </DropdownItem>

          <DropdownItem
            leftIcon={<Trash size={14} className="text-red-600" />}
            onClick={handleDeleteColumn}
            className="text-red-600 hover:bg-gray-100"
          >
            Delete column
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
    </div>
  );
};

interface DeleteColumnConfirmProps {
  columnId: string | null;
  columns: TaskColumn[];
  onConfirm: () => void;
  onCancel: () => void;
}

// Use React.memo to add the static flag React is expecting
export const DeleteColumnConfirm = React.memo(function DeleteColumnConfirm({
  columnId,
  columns,
  onConfirm,
  onCancel,
}: DeleteColumnConfirmProps) {
  if (!columnId) {
    return null;
  }

  const columnToDelete = columns.find(c => c.id === columnId);

  if (!columnToDelete) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Delete column</h3>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete the column "{columnToDelete.name}"? This will also delete
          all tasks in this column.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 font-medium"
          >
            Delete column
          </button>
        </div>
      </div>
    </div>
  );
});
